import { DEFAULT_CROP, panPhotoCrop, photoFrameSize, photoSourceRect, zoomPhotoCrop, type CardStyle, type PhotoCrop } from './core';

interface PhotoEditRequest {
  photo: CanvasImageSource;
  width: number;
  height: number;
  style: CardStyle;
  crop: PhotoCrop;
  apply: (crop: PhotoCrop) => void;
}

export function createPhotoEditor() {
  const dialog = document.createElement('dialog');
  dialog.className = 'mc-photo-dialog';
  dialog.setAttribute('aria-labelledby', 'mc-photo-title');
  dialog.innerHTML = `
    <div class="mc-photo-dialog-heading"><h2 id="mc-photo-title">사진 편집</h2><button type="button" data-close aria-label="사진 편집 닫기">×</button></div>
    <p id="mc-photo-instructions">사진을 끌어서 위치를 맞추세요. 방향키로도 움직일 수 있어요.</p>
    <div class="mc-photo-crop-surface"><canvas id="mc-photo-canvas" tabindex="0" role="img" aria-label="드래그로 이동하는 사진 미리보기" aria-describedby="mc-photo-instructions"></canvas></div>
    <div class="mc-photo-zoom-label"><label for="mc-photo-zoom">확대·축소</label><output id="mc-photo-percent" for="mc-photo-zoom">100%</output></div>
    <div class="mc-photo-zoom"><button type="button" data-zoom-out aria-label="사진 축소">−</button><input id="mc-photo-zoom" type="range" min="1" max="3" step=".01" value="1"/><button type="button" data-zoom-in aria-label="사진 확대">+</button></div>
    <p class="mc-photo-crop-note">선택한 카드의 사진 영역에 맞춰 보여요. 더 움직이려면 조금 확대해 주세요.</p>
    <button type="button" class="mc-photo-reset">원래 위치로</button>
    <div class="mc-photo-dialog-actions"><button type="button" class="button button-soft" data-cancel>취소</button><button type="button" class="button button-primary" data-apply>적용하기</button></div>`;
  document.body.append(dialog);
  const canvas = dialog.querySelector<HTMLCanvasElement>('canvas')!;
  const ctx = canvas.getContext('2d');
  const slider = dialog.querySelector<HTMLInputElement>('input')!;
  const percent = dialog.querySelector<HTMLOutputElement>('output')!;
  const zoomOut = dialog.querySelector<HTMLButtonElement>('[data-zoom-out]')!;
  const zoomIn = dialog.querySelector<HTMLButtonElement>('[data-zoom-in]')!;
  let request: PhotoEditRequest | null = null;
  let draft = { ...DEFAULT_CROP };
  let drag: { id: number; x: number; y: number } | null = null;
  let opener: HTMLElement | null = null;

  function paint() {
    if (!request || !ctx) return;
    const rect = photoSourceRect(request.width, request.height, canvas.width, canvas.height, draft);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(request.photo, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, canvas.width, canvas.height);
    if (drag) {
      ctx.strokeStyle = '#ffffffae'; ctx.lineWidth = 2;
      ctx.beginPath();
      for (const fraction of [1 / 3, 2 / 3]) {
        ctx.moveTo(canvas.width * fraction, 0); ctx.lineTo(canvas.width * fraction, canvas.height);
        ctx.moveTo(0, canvas.height * fraction); ctx.lineTo(canvas.width, canvas.height * fraction);
      }
      ctx.stroke();
    }
    slider.value = String(draft.zoom);
    percent.value = Math.round(draft.zoom * 100) + '%';
    zoomOut.disabled = draft.zoom <= 1;
    zoomIn.disabled = draft.zoom >= 3;
  }

  function move(dx: number, dy: number) {
    if (!request) return;
    draft = panPhotoCrop(request.width, request.height, canvas.width, canvas.height, draft, dx, dy);
    paint();
  }

  function zoom(value: number) {
    if (!request) return;
    draft = zoomPhotoCrop(request.width, request.height, canvas.width, canvas.height, draft, value);
    paint();
  }

  function endDrag() {
    const previous = drag; drag = null;
    if (previous && canvas.hasPointerCapture(previous.id)) canvas.releasePointerCapture(previous.id);
    canvas.classList.remove('is-dragging');
    paint();
  }

  canvas.addEventListener('pointerdown', (event) => {
    if (!request || drag || !event.isPrimary || event.button !== 0) return;
    event.preventDefault(); canvas.focus({ preventScroll: true });
    drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
    canvas.setPointerCapture(event.pointerId); canvas.classList.add('is-dragging'); paint();
  });
  canvas.addEventListener('pointermove', (event) => {
    if (!drag || drag.id !== event.pointerId) return;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    move((event.clientX - drag.x) * canvas.width / bounds.width, (event.clientY - drag.y) * canvas.height / bounds.height);
    drag = { ...drag, x: event.clientX, y: event.clientY };
  });
  for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
    canvas.addEventListener(name, (event) => { if (drag?.id === event.pointerId) endDrag(); });
  }
  canvas.addEventListener('keydown', (event) => {
    const directions: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const direction = directions[event.key];
    if (!direction) return;
    event.preventDefault(); const step = event.shiftKey ? 36 : 10;
    move(direction[0] * step, direction[1] * step);
  });
  slider.addEventListener('input', () => zoom(Number(slider.value)));
  zoomOut.addEventListener('click', () => zoom(draft.zoom - .1));
  zoomIn.addEventListener('click', () => zoom(draft.zoom + .1));
  dialog.querySelector('.mc-photo-reset')!.addEventListener('click', () => { draft = { ...DEFAULT_CROP }; paint(); });
  for (const button of dialog.querySelectorAll('[data-close], [data-cancel]')) button.addEventListener('click', () => dialog.close());
  dialog.querySelector('[data-apply]')!.addEventListener('click', () => {
    request?.apply({ ...draft }); dialog.close();
  });
  dialog.addEventListener('close', () => {
    endDrag(); request = null;
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    opener?.focus({ preventScroll: true }); opener = null;
  });

  return (next: PhotoEditRequest) => {
    if (!ctx || dialog.open) return;
    request = next; draft = { ...next.crop };
    const frame = photoFrameSize(next.style);
    canvas.width = frame.width; canvas.height = frame.height;
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    paint(); dialog.showModal(); canvas.focus({ preventScroll: true });
  };
}
